import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { bankingApi } from '@/lib/banking-api'
import { RealRateDisplay } from '@/components/banking/RealRateDisplay'

const FALLBACK_CORRIDORS = ['EUR', 'GBP', 'CHF', 'PLN', 'SEK', 'NOK', 'DKK']

/** Public rate calculator (USP #3). Mid-market shown above our rate. */
export function FxCalculator() {
  const { t, i18n } = useTranslation('banking')
  const ratesQ = useQuery({ queryKey: ['fx-rates'], queryFn: () => bankingApi.fxRates() })
  const corridors = ratesQ.data?.corridors ?? FALLBACK_CORRIDORS

  const [base, setBase] = useState('EUR')
  const [quote, setQuote] = useState('GBP')
  const [amount, setAmount] = useState('1000')

  const quoteQ = useQuery({
    queryKey: ['fx-quote', base, quote],
    queryFn: () => bankingApi.fxQuote(base, quote),
    enabled: base !== quote,
  })

  const receive = quoteQ.data
    ? new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(
        (Number.parseFloat(amount || '0') || 0) * quoteQ.data.our_sell_rate,
      )
    : '—'

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3">
        <label className="text-sm">
          <span className="text-text-muted">{t('fx.from')}</span>
          <select className="clari-input mt-1 w-full" value={base} onChange={(e) => setBase(e.target.value)}>
            {corridors.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-text-muted">{t('fx.to')}</span>
          <select className="clari-input mt-1 w-full" value={quote} onChange={(e) => setQuote(e.target.value)}>
            {corridors.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="text-sm">
        <span className="text-text-muted">{t('fx.you_send')}</span>
        <input
          className="clari-input mt-1 w-full"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </label>

      {quoteQ.data ? (
        <>
          <div className="rounded-xl border border-border p-3">
            <p className="text-xs text-text-muted">{t('fx.they_get')}</p>
            <p className="font-mono text-2xl">
              {receive} {quote}
            </p>
          </div>
          <RealRateDisplay quote={quoteQ.data} locale={i18n.language} />
          <p className="text-xs text-text-subtle">
            {t('fx.transparent_note', { margin: quoteQ.data.margin_percent })}
          </p>
        </>
      ) : base === quote ? (
        <p className="text-sm text-text-muted">{t('fx.unsupported')}</p>
      ) : null}
    </div>
  )
}
